import { marketMeasurements } from '@pogi/market-units';
import { StandardEvents } from '@shopify/events';
import {
  getIntersectionRoot,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const ownerForNode = (node) =>
  node instanceof Element ? node.closest('product-form-component') : null;
const ADD_EVENT_WAIT_MS = 8000;
const ADD_LIFECYCLE_WAIT_MS = 60000;

/*
 * pogi-sticky-atc
 * On mobile, keeps purchasing within reach while choosing a product. Desktop
 * retains the shortcut after the main purchase action has scrolled past. The
 * bar hides again as the footer approaches so it never covers the cross-sell /
 * footer (§ sticky-ATC redesign). The bar's button submits the native product
 * form via its [form=...] ref, so it adds the current variant + selling-plan
 * selection through Horizon's cart.
 */
class PogiStickyAtc extends HTMLElement {
  connectedCallback() {
    this.connectionGeneration = (this.connectionGeneration || 0) + 1;
    const selector = this.dataset.target || '.pogi-buy-box';
    const target = document.querySelector(selector);
    if (!target || !('IntersectionObserver' in window)) return;
    this.target = target;
    const targetUsesReadyGate = target.getAttribute('aria-busy') === 'true';
    this.buyboxReady = target.hasAttribute('data-ready') || !targetUsesReadyGate;
    this.onBuyboxReady = () => {
      this.buyboxReady = true;
      this.releaseBuyboxGate();
    };
    if (!this.buyboxReady) target.addEventListener('pogi:buybox-ready', this.onBuyboxReady, { once: true });

    // i18n: JA override for the JS-rendered sold-out label, from the
    // [data-satc-i18n] blob the block renders via `| t`. EN fallback stays inline so
    // a missing key can never change the EN site.
    this.I = {};
    const i18nEl = this.querySelector('[data-satc-i18n]');
    if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallback */ } }
    this.t = (k, fb) => (this.I && this.I[k]) || (window.POGI_I18N && window.POGI_I18N[k]) || fb;

    this.pastBuyBox = false;
    this.buyBtn = target.querySelector('[data-pogi-atc]');
    this.buyLabel = target.querySelector('[data-pogi-atc-label]');
    this.stickyBtn = this.querySelector('.pogi-satc__btn');

    // Hide-anchors = the bottom-region bars the sticky must not overlap: the cross-sell's
    // OWN add bar AND the footer. It hides only while one of these is IN/approaching the
    // viewport → shows in the middle, fades at the cross-sell bar, REAPPEARS in the gap
    // after it, then fades again at the footer.
    this.stops = [
      document.querySelector('.pogi-routine__bar'),
      document.querySelector('[data-pogi-footer], .pogi-foot, footer'),
    ].filter(Boolean);

    // Mobile choices can span several screens before the main purchase action.
    // Start at the first product selector; desktop retains its later trigger.
    this.startEl = this.buyBtn || target;
    this.choicesEls = Array.from(target.querySelectorAll('[data-pogi-formats], [data-pogi-sizes], [data-pogi-tiles]'));
    if (!this.choicesEls.length) this.choicesEls.push(target);
    this.barInner = this.querySelector('.pogi-satc__inner');
    this.onScroll = () => this.sync();
    this.onScrollOwnerChange = () => {
      this.bindScrollOwner();
      this.sync();
    };
    this.bindScrollOwner();
    scrollContainerMediaQuery.addEventListener('change', this.onScrollOwnerChange);
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onFocusOut = () => queueMicrotask(() => this.sync());
    this.addEventListener('focusout', this.onFocusOut);
    this.onOverlayToggle = () => this.sync();
    document.addEventListener('toggle', this.onOverlayToggle, true);
    // Native keyboard focus does not account for a fixed dock. Keep focused
    // choices above it without moving the shopper during ordinary scrolling.
    this.onChoiceFocus = (event) => {
      const control = event.target;
      requestAnimationFrame(() => {
        if (!this.isConnected || document.activeElement !== control || !window.matchMedia('(max-width: 749px)').matches) return;
        this.sync();
        if (!this.classList.contains('is-visible')) return;
        const rect = control.getBoundingClientRect();
        const clearBottom = window.innerHeight - (this.barInner?.offsetHeight || 0) - 12;
        if (rect.bottom > clearBottom && rect.top < window.innerHeight) {
          window.scrollBy({ top: rect.bottom - clearBottom, behavior: 'instant' });
        }
      });
    };
    target.addEventListener('focusin', this.onChoiceFocus);

    // Track Horizon's resolved product-selection event: swap the thumbnail +
    // variant line so mobile shoppers can SEE what they selected while the
    // gallery is offscreen.
    this.productId = this.dataset.productId;
    try { this.imgs = JSON.parse(this.querySelector('[data-satc-imgs]').textContent) || {}; }
    catch (_) { this.imgs = {}; }
    this.onVariant = (e) => {
      if (!(e.target instanceof Element) || e.target.closest('product-card')) return;
      e.promise?.then(({ detail }) => {
        const v = detail?.resource;
        const pid = detail?.productId;
        if (!v || String(pid || '') !== String(this.productId)) return;
        const vt = this.querySelector('[data-satc-variant]');
        if (vt && v.title) this.setTranslatedText(vt, marketMeasurements(v.title, this.dataset.country).replace(/ \/ /g, ' · '));
        const im = this.querySelector('[data-satc-img]');
        const src = this.imgs[String(v.id)];
        if (im && src) im.src = src;
      }).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pogi-sticky-atc] product selection failed:', error);
      });
    };
    document.addEventListener(StandardEvents.productSelect, this.onVariant);

    // Post-add feedback (§ "money moves" #3, 2026-06-12): after OUR button adds,
    // flash "✓ Added" for a beat, then restore. Armed only by a sticky-bar click
    // so buy-box adds don't trigger it; confirmed by the standard cart-lines update that follows.
    if (this.stickyBtn) {
      // POGI (2026-06-30): instant "Adding…" on click (matches the buy-box CTA), then
      // "✓ Added" on the confirming cart-lines update, then restore. Armed by a sticky click
      // so buy-box adds don't trigger it. Skips the false "✓ Added" on failed adds and
      // self-reverts if no cart-lines update confirms.
      this.onStickyClick = () => {
        // Product Form owns this lock from the accepted submit through the
        // authoritative drawer morph. Ignore any synthetic/re-enabled click
        // that arrives inside that same shopper intent.
        if (this.stickyBtn.dataset.pogiAddLock === '1') return;
        this.setTranslatedText(this.querySelector('[data-satc-error]'), '');
        this.addGeneration = (this.addGeneration || 0) + 1;
        this.addArmedAt = Date.now();
        this.addFallbackMs = ADD_EVENT_WAIT_MS;
        this.addOwner = ownerForNode(this.stickyBtn.form);
        if (this.addedHtml == null) this.addedHtml = this.stickyBtn.innerHTML; // capture original once
        clearTimeout(this.addedTimer);
        this.stickyBtn.classList.add('is-adding');
        this.stickyBtn.setAttribute('aria-busy', 'true');
        this.stickyBtn.innerHTML = this.t('adding', 'Adding…');
        this.addedTimer = setTimeout(() => this.restoreStickyBtn(), ADD_EVENT_WAIT_MS); // no-event fallback
      };
      this.stickyBtn.addEventListener('click', this.onStickyClick);
      this.onCartUpdate = (e) => {
        if (!this.addArmedAt || Date.now() - this.addArmedAt > ADD_EVENT_WAIT_MS) return;
        if (e.action !== 'add' || !this.addOwner || ownerForNode(e.target) !== this.addOwner) return;
        clearTimeout(this.addedTimer);
        this.addFallbackMs = ADD_LIFECYCLE_WAIT_MS;
        this.addedTimer = setTimeout(() => this.restoreStickyBtn(), ADD_LIFECYCLE_WAIT_MS);
        const connectionGeneration = this.connectionGeneration;
        const addGeneration = this.addGeneration;
        e.promise?.then(({ detail }) => {
          if (!this.isConnected || this.connectionGeneration !== connectionGeneration) return;
          if (this.addGeneration !== addGeneration) {
            this.restoreStickyBtn({ keepArm: true });
            return;
          }
          if (detail?.didError) { this.restoreStickyBtn(); return; }
          const settleSuccess = () => {
            if (!this.isConnected || this.connectionGeneration !== connectionGeneration) return;
            if (this.addGeneration !== addGeneration) return;
            this.addArmedAt = 0;
            clearTimeout(this.addedTimer);
            if (this.addedHtml == null) this.addedHtml = this.stickyBtn.innerHTML;
            this.stickyBtn.classList.remove('is-adding');
            this.stickyBtn.removeAttribute('aria-busy');
            this.stickyBtn.innerHTML = this.t('added', 'Added ✓');
            this.addedTimer = setTimeout(() => this.restoreStickyBtn(), 3200);
          };
          const rendered = e.pogiSectionRenderPromise;
          if (rendered && typeof rendered.then === 'function') {
            rendered.then(settleSuccess, settleSuccess);
          } else {
            settleSuccess();
          }
        }).catch(() => {
          if (!this.isConnected || this.connectionGeneration !== connectionGeneration) return;
          if (this.addGeneration !== addGeneration) {
            this.restoreStickyBtn({ keepArm: true });
            return;
          }
          this.restoreStickyBtn();
        });
      };
      document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
      this.onCartError = (event) => {
        if (!this.addArmedAt || ownerForNode(event.target) !== this.addOwner) return;
        this.setTranslatedText(this.querySelector('[data-satc-error]'),
          event.error || event.detail?.error || window.Theme?.translations?.add_to_cart_error || 'Error adding to cart');
        this.restoreStickyBtn();
      };
      document.addEventListener(StandardEvents.cartError, this.onCartError);
    }

    // Mirror the buy box's live ATC label (price + Subscribe/One-time) so the sticky
    // never shows a stale "From" price. Decoupled — reads the buy box's own rendered
    // output (works for all four buy boxes), and disables when the buy box is sold out.
    if (this.buyLabel) {
      this.priceMo = new MutationObserver(() => this.syncPrice());
      this.priceMo.observe(this.buyLabel, { childList: true, characterData: true, subtree: true });
      if (this.buyBtn) this.priceMo.observe(this.buyBtn, { attributes: true, attributeFilter: ['disabled'] });
      // A cadence change can leave the price unchanged. Watch the selected
      // controls as well, so the summary always describes the current delivery.
      this.priceMo.observe(target, { attributes: true, attributeFilter: ['aria-checked'], subtree: true });
      this.syncPrice();
    }

    if (this.buyboxReady) this.releaseBuyboxGate();
    else if (this.stickyBtn) this.stickyBtn.disabled = true;
  }

  bindScrollOwner() {
    this.observer?.disconnect();
    this.pastBuyBox = false;
    const intersectionRoot = getIntersectionRoot();
    this.intersectionRoot = intersectionRoot;
    this.observer = new IntersectionObserver(
      () => this.sync(),
      { root: intersectionRoot, threshold: 0 }
    );
    this.observer.observe(this.startEl);
    this.choicesEls.forEach((el) => this.observer.observe(el));

    const nextScrollTarget = getScrollEventTarget();
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = nextScrollTarget;
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
  }

  isBuyBoxFallback() {
    return this.closest('.shopify-section')?.hasAttribute('data-pogi-buy-box-fallback') === true;
  }

  syncPrice() {
    if (this.isBuyBoxFallback()) {
      if (this.stickyBtn) this.stickyBtn.disabled = true;
      return;
    }
    if (!this.buyboxReady) {
      if (this.stickyBtn) this.stickyBtn.disabled = true;
      return;
    }
    if (!this.buyLabel) return;
    var label = (this.buyLabel.textContent || '').trim();        // "Start my routine · $17.99" | "カートに追加・¥1,399" | "Sold out"
    // Product Form temporarily disables every form-associated Pogi CTA while
    // an authoritative add is in flight. That is a busy lock, not inventory
    // state, so never rewrite the sticky bar to the false claim "Sold out".
    var addLocked = this.buyBtn && this.buyBtn.dataset.pogiAddLock === '1';
    var stickyAddLocked = this.stickyBtn && this.stickyBtn.dataset.pogiAddLock === '1';
    var soldOut = (this.buyBtn && this.buyBtn.disabled && !addLocked) || /sold out/i.test(label);
    // The translated CTA owns the separator. Most locales use U+00B7 MIDDLE DOT;
    // Japanese uses U+30FB KATAKANA MIDDLE DOT. Purchase mode must come from the
    // buy box's language-independent state, never from English words in the label.
    var idx = Math.max(label.lastIndexOf('·'), label.lastIndexOf('・'));
    var price = idx >= 0 ? label.slice(idx + 1).trim() : '';
    var modeControls = this.target ? this.target.querySelectorAll('[data-pogi-mode]') : [];
    var checkedMode = this.target && this.target.querySelector('[data-pogi-mode][aria-checked="true"]');
    // A product with no selling plans (currently the dispenser) intentionally
    // renders no mode controls and is definitively one-time. If controls do
    // exist, one valid checked control is required before the sticky can enable.
    var mode = modeControls.length === 0 ? 'onetime' : (checkedMode && checkedMode.dataset.pogiMode);
    var modeKnown = mode === 'subscribe' || mode === 'onetime';
    var subscribe = mode === 'subscribe';
    // Transient or malformed labels ("Adding…" / "✓ Added" / missing price) and
    // unknown purchase state fail closed. The next valid buy-box mutation retries.
    if (!soldOut && (idx < 0 || !price || !modeKnown)) {
      if (this.stickyBtn) this.stickyBtn.disabled = true;
      return;
    }
    var setAll = (root, sel, txt) => root.querySelectorAll(sel).forEach((element) => this.setTranslatedText(element, txt));
    const quantity = Number(this.target.querySelector('[data-pogi-qty]')?.textContent.trim() || 1);
    setAll(this, '[data-satc-quantity]', Number.isSafeInteger(quantity) && quantity > 1 ? ' × ' + quantity : '');
    var mobile = this.querySelector('[data-satc-mobile]');
    if (soldOut || !price) {
      var soldOutTxt = this.t('sold_out', 'Sold out');
      setAll(this, '[data-satc-price]', soldOutTxt);
      setAll(this, '[data-satc-mode]', '');
      setAll(this, '[data-satc-interval]', '');
      this.setTranslatedText(mobile, soldOutTxt);
      if (this.stickyBtn) this.stickyBtn.disabled = true;
    } else {
      var modeText = subscribe ? this.t('mode_sub', 'with Subscribe & Save') : this.t('mode_onetime', 'One-time');
      setAll(this, '[data-satc-price]', price);
      setAll(this, '[data-satc-mode]', modeText);
      this.setTranslatedText(mobile, price + ' · ' + modeText);
      const frequency = subscribe
        ? (this.target.querySelector('[data-pogi-interval-select]')?.selectedOptions?.[0]?.textContent.trim()
          || this.target.querySelector('[data-pogi-freq][aria-checked="true"]')?.textContent.trim())
        : '';
      setAll(this, '[data-satc-interval]', frequency
        ? this.t('delivery_interval', 'Delivery interval') + ': ' + frequency
        : '');
      if (this.stickyBtn) this.stickyBtn.disabled = Boolean(addLocked || stickyAddLocked);
    }
  }

  setTranslatedText(element, value) {
    if (!element) return;
    if (typeof window.POGI_SET_TRANSLATED_TEXT === 'function') {
      window.POGI_SET_TRANSLATED_TEXT(element, value);
    } else {
      element.textContent = value == null ? '' : String(value);
    }
  }

  restoreStickyBtn({ keepArm = false } = {}) {
    if (!this.stickyBtn) return;
    const armedAt = this.addArmedAt;
    const owner = this.addOwner;
    clearTimeout(this.addedTimer);
    this.stickyBtn.classList.remove('is-adding');
    this.stickyBtn.removeAttribute('aria-busy');
    if (this.addedHtml != null) { this.stickyBtn.innerHTML = this.addedHtml; this.addedHtml = null; }
    const remaining = (this.addFallbackMs || ADD_EVENT_WAIT_MS) - (Date.now() - armedAt);
    if (keepArm && armedAt && owner && remaining > 0) {
      this.addArmedAt = armedAt;
      this.addOwner = owner;
      this.addedTimer = setTimeout(() => this.restoreStickyBtn(), remaining);
      return;
    }
    this.addArmedAt = 0;
    this.addOwner = null;
    this.addFallbackMs = ADD_EVENT_WAIT_MS;
  }

  releaseBuyboxGate() {
    this.setAttribute('aria-busy', 'false');
    this.syncPrice();
    this.sync();
  }

  sync() {
    if (this.isBuyBoxFallback()) {
      this.classList.remove('is-visible');
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      if (this.stickyBtn) this.stickyBtn.disabled = true;
      return;
    }
    // Hide only while a conflicting bottom bar (cross-sell add bar / footer) is in or
    // approaching the viewport — NOT once it has scrolled fully ABOVE (top goes negative),
    // which was why it never came back after the cross-sell.
    const vh = window.innerHeight;
    const stopNear = this.stops.some((el) => {
      const r = el.getBoundingClientRect();
      return r.top < vh + 80 && r.bottom > 0;
    });
    const focusInside = this.contains(document.activeElement);
    const overlayOpen = Boolean(document.querySelector('dialog[open], header-drawer details[open]'));
    // A scroll jump can move the action from below to above without ever
    // intersecting. Read its position on scroll too, not only on IO crossings.
    const actionRect = this.startEl.getBoundingClientRect();
    const rootTop = this.intersectionRoot?.getBoundingClientRect().top ?? 0;
    this.pastBuyBox = actionRect.height > 0 && actionRect.bottom <= rootTop;
    let shortcutNeeded = this.pastBuyBox;
    if (window.matchMedia('(max-width: 749px)').matches) {
      // Some families render an empty pack container before their real choices
      // (the dispenser starts with scent). Ignore empty/hidden containers.
      const choicesRect = this.choicesEls.map((el) => el.getBoundingClientRect()).find((rect) => rect.height > 0);
      const barHeight = this.barInner?.offsetHeight || 0;
      const headerRect = document.querySelector('#header-component')?.getBoundingClientRect();
      const clearTop = Math.max(0, headerRect?.top <= 0 ? headerRect.bottom : 0);
      // Use the dock's measured height even when hidden, so showing/hiding it
      // cannot oscillate as the main button approaches the bottom edge.
      const clearBottom = vh - barHeight - 12;
      const mainActionVisible = actionRect.height > 0 && actionRect.top >= clearTop && actionRect.bottom <= clearBottom;
      shortcutNeeded = Boolean(choicesRect && choicesRect.top < clearBottom && !mainActionVisible);
    }
    const visible = this.buyboxReady && !overlayOpen && (
      focusInside
      || (shortcutNeeded && !stopNear)
    );
    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.priceMo?.disconnect();
    if (this.onVariant) document.removeEventListener(StandardEvents.productSelect, this.onVariant);
    if (this.onCartUpdate) document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
    if (this.onCartError) document.removeEventListener(StandardEvents.cartError, this.onCartError);
    if (this.target && this.onBuyboxReady) this.target.removeEventListener('pogi:buybox-ready', this.onBuyboxReady);
    if (this.stickyBtn && this.onStickyClick) {
      this.stickyBtn.removeEventListener('click', this.onStickyClick);
    }
    this.addGeneration = (this.addGeneration || 0) + 1;
    this.restoreStickyBtn();
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = null;
    window.removeEventListener('resize', this.onScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.onScrollOwnerChange);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('toggle', this.onOverlayToggle, true);
    this.target?.removeEventListener('focusin', this.onChoiceFocus);
  }
}

if (!customElements.get('pogi-sticky-atc')) {
  customElements.define('pogi-sticky-atc', PogiStickyAtc);
}
